/**
 * React hook to determine if a product is associated with a corporate sale or employee promotion.
 *
 * Checks the product's productPromotions array for any promotionId containing
 * 'corp-sale' or 'employee'. Returns an object with boolean flag and the corporate sale label.
 *
 * @param {Object} product - The product object, expected to have a productPromotions array.
 * @returns {Object} result - Object containing:
 *   - isCorporateSale {boolean} - True if a relevant promotion is found, false otherwise.
 *   - corporateSaleLabelName {string|null} - The calloutMsg of the product if it exists, null otherwise.
 *
 *
 * Also exports a helper function `containsCorpSaleOrEmployee` to check if a given string contains
 * 'corp-sale' or 'employee'.
 *
 * @param {string} str - The string to check.
 * @returns {boolean} - True if the string contains 'corp-sale' or 'employee', false otherwise.
 */

import {useMemo} from 'react'

export function containsCorpSaleOrEmployee(str) {
    return str.includes('corp-sale') || str.includes('employee')
}

export default function useCorporateSale(product) {
    const result = useMemo(() => {
        const isCorporateSale =
            product?.productPromotions?.some((promo) =>
                containsCorpSaleOrEmployee(promo?.promotionId || ''),
            ) || false

        const corporateSaleLabelName =
            product?.productPromotions?.find((promo) =>
                containsCorpSaleOrEmployee(promo?.promotionId || ''),
            )?.calloutMsg || null

        return {
            isCorporateSale,
            corporateSaleLabelName,
        }
    }, [product])

    return result
}
